import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Hero from '@/components/feature/Hero';
import CategorySection from '@/components/feature/CategorySection';
import ProductsSection from '@/components/feature/ProductsSection';
import FeaturesSection from '@/components/feature/FeaturesSection';
import ArticlesPreview from '@/components/feature/ArticlesPreview';
import CTASection from '@/components/feature/CTASection';
import Footer from '@/components/feature/Footer';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_NAME, SITE_FAVICON, siteOrigin } from '@/lib/seo';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const handleLangChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const location = useLocation();
  const origin = siteOrigin();
  const homePath = location.pathname === '/' ? '/' : location.pathname;

  useSeo({
    title: 'AllChinaBuy Spreadsheet – Products, Sellers & Buying Guides',
    description:
      'Explore the AllChinaBuy spreadsheet with product lists, seller guides, QC tips, shipping information, reviews and buying resources.',
    keywords:
      'AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy shipping cost, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse, AllChinaBuy legit, AllChinaBuy buying guide',
    path: homePath,
    type: 'website',
    image:
      'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20fashion%20flat%20lay%20photography%20overhead%20view%20of%20premium%20clothing%20shoes%20and%20accessories%20arranged%20artistically%20on%20clean%20light%20beige%20surface%2C%20soft%20natural%20window%20light%2C%20luxury%20editorial%20style%2C%20warm%20neutral%20earth%20tones%20cream%20beige%20white%2C%20high-end%20retail%20aesthetic%2C%20pristine%20composition&width=1600&height=900&seq=hero-bg&orientation=landscape',
  });

  useJsonLd([
    {
      id: 'website',
      data: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: origin ? `${origin}/` : '/',
        description:
          'Explore the AllChinaBuy spreadsheet with product lists, seller guides, QC tips, shipping information, reviews and buying resources.',
        inLanguage: ['en', 'fr', 'da', 'de'],
      },
    },
    {
      id: 'organization',
      data: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: origin ? `${origin}/` : '/',
        logo: SITE_FAVICON,
      },
    },
  ]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLang={currentLang} onLangChange={handleLangChange} />
      <Hero />
      <CategorySection />
      <ProductsSection />
      <FeaturesSection />
      <ArticlesPreview />
      <CTASection />
      <Footer />
    </div>
  );
}